<template>
	<div class="announcement-bell-container">
		<v-menu
			v-model="showMenu"
			bottom
			offset-y
			:nudge-left="isPC ? 100 : 50"
			:min-width="isPC ? 380 : 300"
			:max-width="isPC ? 400 : 320"
			max-height="500"
			:close-on-content-click="false"
			transition="scale-transition"
			origin="top right"
		>
			<template v-slot:activator="{ on }">
				<button v-on="on" class="bell-icon-button" @click="handleBellClick">
					<v-icon :color="safeUnreadCount > 0 ? '#dcc268' : '#999'" size="24">
						{{ safeUnreadCount > 0 ? 'mdi-bell' : 'mdi-bell-outline' }}
					</v-icon>
					<div class="bell-badge" v-if="safeUnreadCount > 0">
						<span :class="['badge', safeUnreadCount >= 10 ? 'badge-capsule' : 'badge-circle']">
							{{ safeUnreadCount }}
						</span>
					</div>
				</button>
			</template>

			<v-card class="announcement-popover" elevation="2">
				<v-card-title class="popover-header">
					<h3>お知らせ</h3>
					<span v-if="safeUnreadCount > 0" class="unread-count-text">未読: {{ safeUnreadCount }}件</span>
				</v-card-title>

				<v-divider />

				<div class="announcement-list-container">
					<div v-if="loading" class="loading-state">
						<v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
						<span class="loading-text">読み込み中...</span>
					</div>

					<div v-else-if="safeAnnouncements.length === 0" class="empty-state">
						<v-icon color="grey lighten-1">mdi-bell-outline</v-icon>
						<span class="empty-text">お知らせはありません</span>
					</div>

					<div v-else class="announcements-scroll">
						<div
							v-for="announcement in safeAnnouncements"
							:key="announcement.id"
							class="announcement-item"
							:class="{ unread: !announcement.isRead }"
							@click="openAnnouncement(announcement)"
						>
							<div class="item-header">
								<div class="title-container">
									<span class="item-title" :class="{ 'font-weight-bold': !announcement.isRead }">
										{{ announcement.title }}
									</span>
								</div>
								<div class="item-meta">
									<span class="item-date">{{ formatDate(announcement.createdAt) }}</span>
									<v-chip v-if="!announcement.isRead" x-small color="primary" text-color="white" class="unread-chip">
										未読
									</v-chip>
								</div>
							</div>
							<div class="item-preview">
								<p class="preview-text">{{ getPreviewText(announcement.content) }}</p>
							</div>
						</div>
					</div>
				</div>

				<v-divider />

				<v-card-actions class="popover-footer">
					<v-spacer />
					<v-btn text small @click="showMenu = false"> 閉じる </v-btn>
				</v-card-actions>
			</v-card>
		</v-menu>

		<!-- お知らせ詳細ダイアログ -->
		<AnnouncementDetailDialog
			v-if="selectedAnnouncement"
			:announcement="selectedAnnouncement"
			:show="showDetailDialog"
			@close="closeDetailDialog"
			@mark-as-read="markAsRead"
		/>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AnnouncementDetailDialog from '../Organisms/AnnouncementDetailDialog'

export default {
	name: 'AnnouncementBellIcon',
	components: {
		AnnouncementDetailDialog
	},
	data() {
		return {
			showMenu: false,
			showDetailDialog: false,
			selectedAnnouncement: null
		}
	},
	mounted() {
		// 初回マウント時にお知らせ情報を取得
		if (this.userInfo.accessToken && this.userInfo.studentId) {
			this.fetchAnnouncements({
				accessToken: this.userInfo.accessToken,
				studentId: this.userInfo.studentId
			})
		}
	},
	computed: {
		...mapGetters({
			announcements: 'announcement/announcements',
			loading: 'announcement/loading',
			unreadCount: 'announcement/unreadCount',
			userInfo: 'user/getUserInfo'
		}),
		// 安全な配列の取得
		safeAnnouncements() {
			return this.announcements || []
		},
		safeUnreadCount() {
			return this.unreadCount || 0
		},
		isPC() {
			return this.$vuetify.breakpoint.smAndUp
		}
	},
	methods: {
		...mapActions({
			fetchAnnouncements: 'announcement/fetchAnnouncements',
			markAnnouncementAsRead: 'announcement/markAnnouncementAsRead'
		}),
		async handleBellClick() {
			// ベルアイコンクリック時にお知らせを取得
			if (this.userInfo.accessToken && this.userInfo.studentId) {
				await this.fetchAnnouncements({
					accessToken: this.userInfo.accessToken,
					studentId: this.userInfo.studentId
				})
			}
		},
		openAnnouncement(announcement) {
			this.selectedAnnouncement = announcement
			this.showDetailDialog = true
			this.showMenu = false

			// ダイアログを開いた時点で未読のお知らせを既読にする
			if (!announcement.isRead) {
				this.markAsRead(announcement.id)
			}
		},
		closeDetailDialog() {
			this.showDetailDialog = false
			this.selectedAnnouncement = null
		},
		async markAsRead(announcementId) {
			await this.markAnnouncementAsRead({
				announcementId,
				studentId: this.userInfo.studentId,
				accessToken: this.userInfo.accessToken
			})
		},
		formatDate(dateString) {
			if (!dateString) {
				return '不明'
			}

			const date = new Date(dateString)

			// 無効な日付の場合のエラーハンドリング
			if (isNaN(date.getTime())) {
				console.warn('Invalid date received:', dateString)
				return '不明'
			}

			const now = new Date()
			const diffTime = now - date
			const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

			if (diffDays === 0) {
				return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
			} else if (diffDays === 1) {
				return '昨日'
			} else if (diffDays < 7) {
				return `${diffDays}日前`
			} else {
				return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })
			}
		},
		getPreviewText(content) {
			return content.length > 50 ? content.substring(0, 50) + '...' : content
		},
		getTypeIcon(type) {
			const icons = {
				SYSTEM: 'mdi-cog',
				LESSON: 'mdi-school',
				CAMPAIGN: 'mdi-star',
				FEATURE: 'mdi-new-box',
				ACCOUNT: 'mdi-account-circle'
			}
			return icons[type] || 'mdi-bell'
		},
		getTypeColor(type) {
			const colors = {
				SYSTEM: 'orange',
				LESSON: 'blue',
				CAMPAIGN: 'pink',
				FEATURE: 'green',
				ACCOUNT: 'purple'
			}
			return colors[type] || 'grey'
		}
	}
}
</script>

<style lang="scss" scoped>
.announcement-bell-container {
	height: 100%;
	display: flex;
	align-items: center;
}

.bell-icon-button {
	position: relative;
	background: none;
	border: none;
	cursor: pointer;
	padding: 8px;
	border-radius: 50%;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: rgba(0, 0, 0, 0.04);
	}
}

.bell-badge {
	position: absolute;
	top: 2px;
	right: 0px;

	.badge {
		background-color: white;
		border: 2px solid #dcc268;
		color: #dcc268;
		font-weight: bold;
		font-size: 0.7rem;
		line-height: 1;
		min-width: 18px;
		height: 18px;
		display: flex;
		align-items: center;
		justify-content: center;

		&.badge-circle {
			border-radius: 50%;
			padding: 0;
		}

		&.badge-capsule {
			border-radius: 10px;
			padding: 1 6px;
			min-width: 26px;
			height: 18px;
		}
	}
}

.announcement-popover {
	border-radius: 8px;
	overflow: hidden;

	.popover-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 20px 12px;
		background-color: #f8f9fa;
		border-bottom: 1px solid #e9ecef;

		h3 {
			margin: 0;
			font-size: 1.1rem;
			font-weight: bold;
			color: #333;
		}

		.unread-count-text {
			background: #dcc268;
			color: white;
			padding: 4px 8px;
			border-radius: 12px;
			font-size: 0.8rem;
			font-weight: bold;
		}
	}

	.announcement-list-container {
		max-height: 320px;
		min-height: 200px;
		overflow-y: auto;

		.loading-state {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 12px;
			padding: 40px 20px;

			.loading-text {
				color: #666;
				font-size: 0.9rem;
			}
		}

		.empty-state {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 8px;
			padding: 40px 20px;

			.empty-text {
				color: #666;
				font-size: 0.9rem;
			}
		}

		.announcements-scroll {
			.announcement-item {
				padding: 16px 20px;
				border-bottom: 1px solid #f0f0f0;
				cursor: pointer;
				transition: background-color 0.2s ease;

				&:hover {
					background-color: #f8f9fa;
				}

				&:last-child {
					border-bottom: none;
				}

				&.unread {
					background-color: #f0f8ff;
					border-left: 3px solid #dcc268;
				}

				.item-header {
					display: flex;
					justify-content: space-between;
					align-items: flex-start;
					margin-bottom: 8px;

					.title-container {
						display: flex;
						align-items: center;
						flex: 1;
						margin-right: 12px;

						.item-title {
							font-size: 0.9rem;
							line-height: 1.3;
							word-break: break-word;
						}
					}

					.item-meta {
						display: flex;
						flex-direction: column;
						align-items: flex-end;
						gap: 4px;

						.item-date {
							font-size: 0.75rem;
							color: #666;
							white-space: nowrap;
						}

						.unread-chip {
							font-size: 0.6rem;
						}
					}
				}

				.item-preview {
					.preview-text {
						font-size: 0.8rem;
						color: #666;
						line-height: 1.4;
						margin: 0;
					}
				}
			}
		}
	}

	.popover-footer {
		padding: 8px 20px 12px;
		background-color: #fafafa;
	}
}

@media (max-width: 600px) {
	.announcement-popover {
		margin-right: 10px; // 画面端からの余裕を確保

		.popover-header {
			padding: 12px 16px 8px;

			h3 {
				font-size: 1rem;
			}

			.unread-count-text {
				font-size: 0.7rem;
			}
		}

		.announcement-list-container {
			.announcements-scroll {
				.announcement-item {
					padding: 12px 16px;

					.item-header {
						.title-container {
							.item-title {
								font-size: 0.85rem;
							}
						}

						.item-meta {
							.item-date {
								font-size: 0.7rem;
							}
						}
					}

					.item-preview {
						.preview-text {
							font-size: 0.75rem;
						}
					}
				}
			}
		}

		.popover-footer {
			padding: 6px 16px 8px;
		}
	}
}
</style>
